// src/pages/Drivers.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import DriverForm from "../components/drivers/DriverForm";
import {
  getDrivers,
  createDriver,
  updateDriver,
  setDriverDisabled,
  deleteDriver,
} from "../services/driverService";

/**
 * Drivers admin page
 *
 * - List all drivers (including disabled — admins need to see them to
 *   re-enable / delete).
 * - Add (paste Firebase Auth UID + profile fields).
 * - Edit (UID is read-only).
 * - Disable / enable (with reminder to also toggle the Auth user in
 *   Firebase Console).
 * - Delete (hard-delete; reminder to also delete the Auth user).
 *
 * Lean intentionally — typical fleet is 1-10 drivers, no need for
 * pagination / complex filters.
 */
const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const all = await getDrivers({ includeDisabled: true });
      setDrivers(all);
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // -------- handlers --------

  const handleAdd = () => {
    setSelectedDriver(null);
    setIsModalOpen(true);
  };

  const handleEdit = (driver) => {
    setSelectedDriver(driver);
    setIsModalOpen(true);
  };

  const handleSave = async (formData) => {
    if (formData.id && drivers.some((d) => d.id === formData.id)) {
      // Editing — split UID from rest of payload
      const { id, ...rest } = formData;
      await updateDriver(id, rest);
    } else {
      // Creating
      const { id, ...rest } = formData;
      await createDriver(id, rest);
    }
    await refresh();
  };

  const handleToggleDisabled = async (driver) => {
    const wantDisabled = !driver.disabled;
    const result = await Swal.fire({
      title: wantDisabled ? "Disable this fitter?" : "Re-enable this fitter?",
      html: wantDisabled
        ? `<p>${driver.name} won't be assignable to new jobs.</p>
           <p class="mt-2 text-sm text-orange-700">
             <strong>Don't forget:</strong> to lock them out of the fitter
             portal, also disable their Auth user in Firebase Console →
             Authentication → Users.
           </p>`
        : `<p>${driver.name} will be assignable again.</p>
           <p class="mt-2 text-sm text-orange-700">
             If you previously disabled their Auth user in Firebase
             Console, re-enable it there too.
           </p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: wantDisabled ? "#f97316" : "#10b981",
      confirmButtonText: wantDisabled ? "Yes, disable" : "Yes, re-enable",
    });
    if (!result.isConfirmed) return;
    try {
      await setDriverDisabled(driver.id, wantDisabled);
      await refresh();
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    }
  };

  const handleDelete = async (driver) => {
    const result = await Swal.fire({
      title: "Delete this fitter?",
      html: `<p>${driver.name} will be permanently removed from this app.</p>
             <p class="mt-2 text-sm text-orange-700">
               <strong>Note:</strong> their Firebase Auth user is NOT
               deleted — do that manually in Firebase Console if needed.
             </p>
             <p class="mt-2 text-sm text-red-700">
               Existing calendar events that were assigned to them will
               keep <code>driverId</code> pointing at a deleted record
               until you reassign. They won't auto-unassign.
             </p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteDriver(driver.id);
      await refresh();
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    }
  };

  // -------- render --------

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fitters</h1>
          <p className="text-gray-600 mt-1">
            Manage fitters and their access to the fitter portal.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add fitter
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : drivers.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 mb-3">No fitters yet.</p>
            <button
              type="button"
              onClick={handleAdd}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Add the first one
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide px-4 py-3">
                  Name
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide px-4 py-3">
                  Email
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide px-4 py-3">
                  Phone
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide px-4 py-3">
                  Vehicle
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide px-4 py-3">
                  Status
                </th>
                <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wide px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {drivers.map((d) => (
                <tr
                  key={d.id}
                  className={d.disabled ? "bg-gray-50 text-gray-500" : ""}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {d.name}
                    <div className="text-xs text-gray-400 font-mono mt-0.5">
                      {d.id}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{d.email}</td>
                  <td className="px-4 py-3 text-sm">{d.phone || "—"}</td>
                  <td className="px-4 py-3 text-sm">
                    {d.licensePlate || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {d.disabled ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                        Disabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    <div className="inline-flex items-center gap-3">
                      <Link
                        to={`/drivers/${d.id}/queue`}
                        className="text-indigo-600 hover:text-indigo-800"
                        title="View this fitter's job queue"
                      >
                        Queue
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleEdit(d)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleDisabled(d)}
                        className={
                          d.disabled
                            ? "text-green-600 hover:text-green-800"
                            : "text-orange-600 hover:text-orange-800"
                        }
                      >
                        {d.disabled ? "Enable" : "Disable"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(d)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <DriverForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDriver(null);
        }}
        driver={selectedDriver}
        onSave={handleSave}
      />
    </div>
  );
};

export default Drivers;
